// S36 對帳工作流(/reconciliations)— Reconciliation(IA specs/01 §2.3)。
// run 匯入(冪等)→ 差異清單(open/investigating/adjusted/accepted/resolved)→
// assign / adjust / resolve workflow;resolution 必附 actor/reason/case。
// 誠實缺口:後端尚無「run 清單」端點(僅 POST 建立冪等回放 + 依 run_id 查 items)
// → 本頁以 run_id 查詢 + 本 session 匯入紀錄快速切換,不造假清單。
// 「retry」= 同 冪等鍵 重送匯入(冪等重放);無 per-item retry 端點。

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError } from '../../lib/api';
import { ErrorState } from '../../components/StateBlock';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { formatTimestampUs } from '../../lib/format';
import { roleLabel } from '../users/userModel';
import '../_shared/shared.css';
import './commerce.css';
import type { ReconciliationItem, ReconciliationRun } from './commerceApi';
import { assignReconciliationItem, createReconciliation, listReconciliationItems, resolveReconciliationItem } from './commerceApi';
import {
  DUAL_CONTROL_NOTE,
  canCommerceWrite,
  formatMinor,
  formatSignedMinor,
  reconItemStatusLabel,
  reconItemStatusTone,
  usFromLocalInput,
} from './commerceModel';

function errorText(e: unknown): string {
  if (e instanceof ApiError) return `${e.message}${e.requestId ? `（request_id: ${e.requestId}）` : ''}`;
  return e instanceof Error ? e.message : '操作失敗';
}

interface ImportItemRow {
  kind: string;
  entity_type: string;
  entity_id: string;
  currency: string;
  expected: string;
  actual: string;
}

const EMPTY_ITEM_ROW: ImportItemRow = { kind: 'amount_mismatch', entity_type: 'invoice', entity_id: '', currency: 'USD', expected: '0', actual: '0' };

const RESOLUTIONS = [
  { value: 'accepted', label: '接受差異(不調帳)' },
  { value: 'adjusted', label: '已調帳(附 adjustment 交易)' },
  { value: 'resolved', label: '其他方式結案' },
] as const;

export default function ReconciliationsPage() {
  const { role } = useAuth();
  const writable = canCommerceWrite(role);
  const [searchParams, setSearchParams] = useSearchParams();

  const [runInput, setRunInput] = useState('');
  const [queriedRun, setQueriedRun] = useState('');
  const [items, setItems] = useState<ReconciliationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [err, setErr] = useState('');

  // 本 session 匯入/檢視過的 run(無 run 清單端點 → 只記真的看過的,不造假)
  const [recentRuns, setRecentRuns] = useState<ReconciliationRun[]>([]);

  // 匯入表單
  const [provider, setProvider] = useState('stripe');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [idemKey, setIdemKey] = useState('');
  const [importRows, setImportRows] = useState<ImportItemRow[]>([{ ...EMPTY_ITEM_ROW }]);
  const [confirmImport, setConfirmImport] = useState(false);

  // 工作流動作
  const [selectedItemId, setSelectedItemId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [resolution, setResolution] = useState('accepted');
  const [resolveReason, setResolveReason] = useState('');
  const [resolveCaseId, setResolveCaseId] = useState('');
  const [adjustmentTxId, setAdjustmentTxId] = useState('');
  const [confirmResolve, setConfirmResolve] = useState(false);

  async function loadItems(runId: string, writeUrl = true) {
    if (!runId.trim()) return;
    setLoading(true);
    setLoadErr(null);
    setNotice('');
    setErr('');
    try {
      const rows = await listReconciliationItems(runId.trim());
      setItems(rows);
      setQueriedRun(runId.trim());
      setRunInput(runId.trim());
      setSelectedItemId('');
      if (writeUrl) setSearchParams({ run: runId.trim() }, { replace: true });
    } catch (e) {
      setLoadErr(e);
      setItems([]);
      setQueriedRun(runId.trim());
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const fromUrl = searchParams.get('run');
    if (fromUrl) void loadItems(fromUrl, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedItem = useMemo(() => items.find((i) => i.item_id === selectedItemId) ?? null, [items, selectedItemId]);
  const openCount = items.filter((i) => i.status === 'open').length;
  const investigatingCount = items.filter((i) => i.status === 'investigating').length;
  const resolvedCount = items.filter((i) => i.status === 'resolved').length;

  const importItemsValid =
    importRows.length > 0 &&
    importRows.length <= 99 &&
    importRows.every(
      (r) =>
        r.kind.trim() &&
        r.entity_type.trim() &&
        r.entity_id.trim() &&
        /^[A-Za-z]{3}$/.test(r.currency.trim()) &&
        Number.isFinite(Number.parseInt(r.expected, 10)) &&
        Number.isFinite(Number.parseInt(r.actual, 10))
    );
  const periodStartUs = usFromLocalInput(periodStart);
  const periodEndUs = usFromLocalInput(periodEnd);
  const importReady = Boolean(
    writable &&
      provider.trim() &&
      idemKey.trim() &&
      periodStartUs !== undefined &&
      periodEndUs !== undefined &&
      periodEndUs > periodStartUs &&
      importItemsValid
  );

  const assignReady = Boolean(writable && selectedItem && (selectedItem.status === 'open' || selectedItem.status === 'investigating') && assignee.trim());
  const resolveReady = Boolean(
    writable &&
      selectedItem &&
      (selectedItem.status === 'open' || selectedItem.status === 'investigating') &&
      resolveReason.trim() &&
      resolveCaseId.trim() &&
      (resolution !== 'adjusted' || adjustmentTxId.trim())
  );

  async function submitImport() {
    setBusy(true);
    try {
      const run = await createReconciliation({
        provider: provider.trim(),
        period_start: periodStartUs!,
        period_end: periodEndUs!,
        idempotency_key: idemKey.trim(),
        items: importRows.map((r) => ({
          kind: r.kind.trim(),
          entity_type: r.entity_type.trim(),
          entity_id: r.entity_id.trim(),
          currency: r.currency.trim().toUpperCase(),
          expected_minor: Number.parseInt(r.expected, 10),
          actual_minor: Number.parseInt(r.actual, 10),
        })),
      });
      setConfirmImport(false);
      setErr('');
      setRecentRuns((rows) => [run, ...rows.filter((r) => r.run_id !== run.run_id)].slice(0, 8));
      setNotice(`已匯入對帳 run ${run.run_id}(差異 ${run.open_items} 筆;delta=0 的列不入庫)。同 key 重送=冪等重放。`);
      void loadItems(run.run_id);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitAssign() {
    if (!selectedItem) return;
    setBusy(true);
    setErr('');
    try {
      const next = await assignReconciliationItem(selectedItem.run_id, selectedItem.item_id, assignee.trim());
      setItems((rows) => rows.map((i) => (i.item_id === next.item_id ? next : i)));
      setNotice(`已指派 ${next.item_id} 給 ${next.assignee_id}(狀態 → 調查中)`);
      setAssignee('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function submitResolve() {
    if (!selectedItem) return;
    setBusy(true);
    try {
      const next = await resolveReconciliationItem(selectedItem.run_id, selectedItem.item_id, {
        resolution,
        reason: resolveReason.trim(),
        case_id: resolveCaseId.trim(),
        adjustment_transaction_id: resolution === 'adjusted' ? adjustmentTxId.trim() : undefined,
      });
      setItems((rows) => rows.map((i) => (i.item_id === next.item_id ? next : i)));
      setConfirmResolve(false);
      setErr('');
      setNotice(`已結案 ${next.item_id}(${reconItemStatusLabel(next)})`);
      setResolveReason('');
      setResolveCaseId('');
      setAdjustmentTxId('');
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="commerce-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">對帳工作流</h1>
          <p className="page-subtitle">gateway/provider 對帳 run 匯入、差異清單與 assign / adjust / resolve 工作流;結案必附 actor / reason / case。</p>
        </div>
        <div className="adm-actionbar-btns">
          <span className={`pill ${writable ? 'pill-good' : 'pill-neutral'}`}>{role ? roleLabel(role) : '其他角色'}</span>
        </div>
      </div>

      {notice && (
        <p className="adm-action-result is-ok" role="status">
          {notice}
        </p>
      )}
      {err && !confirmImport && !confirmResolve && (
        <p className="adm-action-result is-err" role="alert">
          {err}
        </p>
      )}

      <p className="commerce-gap-note">
        誠實缺口:後端尚未提供 run 清單端點(現有=匯入 POST 冪等回放 + 依 run_id 查差異)。本頁以 run_id 查詢;下方「本次 session 匯入」
        只列真的建立/重放過的 run,不偽造歷史清單。歷史 run_id 可自稽核紀錄(action=admin.reconciliation.create)反查。
      </p>

      <div className="kpi-strip" aria-label="對帳統計">
        <div className="kpi-card kpi-card--hero">
          <span className="kpi-label">差異</span>
          <span className="kpi-value">{queriedRun ? items.length : '—'}</span>
          <span className="kpi-foot">{queriedRun ? `run ${queriedRun.slice(0, 18)}…` : '尚未載入 run'}</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">待處理</span>
          <span className="kpi-value">{queriedRun ? openCount : '—'}</span>
          <span className="kpi-foot">open</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-label">調查中</span>
          <span className="kpi-value">{queriedRun ? investigatingCount : '—'}</span>
          <span className="kpi-foot">調查中(已指派)</span>
        </div>
        <div className="kpi-card kpi-card--secondary">
          <span className="kpi-label">已結案</span>
          <span className="kpi-value">{queriedRun ? resolvedCount : '—'}</span>
          <span className="kpi-foot">adjusted / accepted / resolved</span>
        </div>
      </div>

      <div className="commerce-split">
        <section className="card">
          <div className="section-head">
            <div>
              <h2 className="section-title">差異清單</h2>
              <span className="section-meta adm-mono">{queriedRun || '尚未載入'}</span>
            </div>
            {loading && <span className="pill pill-neutral">查詢中</span>}
          </div>
          <div className="commerce-query-row" style={{ marginBottom: 'var(--spacing-6)' }}>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="adm-field-label text-secondary">批次 ID</span>
              <input
                value={runInput}
                onChange={(e) => setRunInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void loadItems(runInput);
                }}
                placeholder="recon-…"
              />
            </label>
            <button className="btn-primary" disabled={loading || !runInput.trim()} onClick={() => void loadItems(runInput)}>
              載入
            </button>
          </div>
          {recentRuns.length > 0 && (
            <div className="commerce-recent-runs" style={{ marginBottom: 'var(--spacing-6)' }}>
              {recentRuns.map((r) => (
                <button key={r.run_id} className="btn-ghost btn-sm" onClick={() => void loadItems(r.run_id)} title={`${r.provider}・${formatTimestampUs(r.created_at, { second: undefined })}・差異 ${r.open_items} 筆`}>
                  <span className="adm-mono">{r.run_id.slice(0, 14)}…</span>({r.provider})
                </button>
              ))}
            </div>
          )}
          {loadErr ? (
            <ErrorState error={loadErr} onRetry={() => void loadItems(runInput)} />
          ) : queriedRun ? (
            items.length > 0 ? (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>差異</th>
                      <th>對象(金流方參照)</th>
                      <th>帳上 / 實收</th>
                      <th>差額</th>
                      <th>狀態</th>
                      <th>處理人 / 案件</th>
                      <th>調帳</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr
                        key={item.item_id}
                        className={`clickable ${selectedItemId === item.item_id ? 'is-selected' : ''}`}
                        onClick={() => {
                          setSelectedItemId(item.item_id === selectedItemId ? '' : item.item_id);
                          setErr('');
                        }}
                      >
                        <td>
                          <div className="adm-mono" title={item.item_id}>
                            {item.item_id.slice(0, 14)}…
                          </div>
                          <div className="text-tertiary">{item.kind}</div>
                        </td>
                        <td>
                          <div className="adm-mono">
                            {item.entity_type}:{item.entity_id}
                          </div>
                          <div className="text-tertiary">{item.provider}</div>
                        </td>
                        <td className="adm-mono">
                          {formatMinor(item.expected_minor, item.currency)} / {formatMinor(item.actual_minor, item.currency)}
                        </td>
                        <td className="adm-mono">
                          <strong>{formatSignedMinor(item.delta_minor, item.currency)}</strong>
                        </td>
                        <td>
                          <span className={`pill ${reconItemStatusTone(item)}`}>{reconItemStatusLabel(item)}</span>
                        </td>
                        <td>
                          <div>{item.assignee_id || item.resolved_by || '—'}</div>
                          <div className="text-tertiary">{item.case_id || ''}</div>
                        </td>
                        <td>
                          {item.adjustment_transaction_id ? (
                            <Link className="adm-mono" to={`/accounting?tx=${encodeURIComponent(item.adjustment_transaction_id)}`} onClick={(e) => e.stopPropagation()}>
                              {item.adjustment_transaction_id.slice(0, 12)}…
                            </Link>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">✅</div>
                <div className="empty-state-title">此批次沒有差異</div>
                <div className="empty-state-detail">匯入時 delta=0 的列不入庫;此 run 對平,或 run_id 有誤。</div>
              </div>
            )
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">🧮</div>
              <div className="empty-state-title">輸入 run_id 或先匯入一次對帳</div>
              <div className="empty-state-detail">run_id 由 provider + 冪等鍵 決定;重送同 key 會回放同一 run。</div>
            </div>
          )}

          {selectedItem && writable && (
            <div className="commerce-form" style={{ marginTop: 'var(--spacing-8)' }}>
              <div className="section-head">
                <div>
                  <h3 className="section-title">
                    處理差異 — <span className="adm-mono">{selectedItem.item_id}</span>
                  </h3>
                  <span className="section-meta">
                    {selectedItem.entity_type}:{selectedItem.entity_id}・差額 {formatSignedMinor(selectedItem.delta_minor, selectedItem.currency)}・
                    {reconItemStatusLabel(selectedItem)}
                  </span>
                </div>
              </div>
              {selectedItem.status === 'resolved' ? (
                <p className="commerce-hint text-tertiary">
                  已結案:{selectedItem.resolution}・{selectedItem.resolution_reason}・案件 {selectedItem.case_id}・處理人 {selectedItem.resolved_by}
                  ;結案後不可重開(如需更正,以新 run 匯入)。
                </p>
              ) : (
                <div className="commerce-form-grid">
                  <div className="commerce-form">
                    <div className="adm-cell-strong">指派調查</div>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">處理人(user id)</span>
                      <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="u-…" disabled={busy} />
                    </label>
                    <button className="btn-ghost" disabled={busy || !assignReady} onClick={() => void submitAssign()}>
                      指派(→ 調查中)
                    </button>
                  </div>
                  <div className="commerce-form">
                    <div className="adm-cell-strong">結案</div>
                    <label className="adm-field">
                      <span className="adm-field-label text-secondary">結案方式</span>
                      <select value={resolution} onChange={(e) => setResolution(e.target.value)} disabled={busy}>
                        {RESOLUTIONS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {resolution === 'adjusted' && (
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">調帳交易 ID(必填)</span>
                        <input value={adjustmentTxId} onChange={(e) => setAdjustmentTxId(e.target.value)} placeholder="ltx-…(先於帳務總帳過帳)" disabled={busy} />
                      </label>
                    )}
                    <div className="commerce-form-grid">
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">原因</span>
                        <input value={resolveReason} onChange={(e) => setResolveReason(e.target.value)} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">案件編號</span>
                        <input value={resolveCaseId} onChange={(e) => setResolveCaseId(e.target.value)} disabled={busy} />
                      </label>
                    </div>
                    {resolution === 'adjusted' && (
                      <span className="commerce-hint text-tertiary">
                        調帳結案流程:先到 <Link to="/accounting">帳務總帳</Link> 過帳 adjustment,再回此附上交易 ID 結案。
                      </span>
                    )}
                    <button className="btn-primary" disabled={busy || !resolveReady} onClick={() => { setErr(''); setConfirmResolve(true); }}>
                      結案
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {selectedItem && !writable && (
            <p className="commerce-hint text-tertiary" style={{ marginTop: 'var(--spacing-6)' }}>
              目前角色僅能檢視差異;assign / resolve 由 admin / platform-owner 執行。
            </p>
          )}
        </section>

        <aside className="commerce-side">
          <section className="card card--primary">
            <div className="section-head">
              <h2 className="section-title">{writable ? '匯入對帳 run' : '唯讀檢視'}</h2>
            </div>
            {writable ? (
              <div className="commerce-form">
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">金流方</span>
                    <input value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="stripe" disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">冪等鍵</span>
                    <input value={idemKey} onChange={(e) => setIdemKey(e.target.value)} placeholder="stripe-2026-07" disabled={busy} />
                  </label>
                </div>
                <div className="commerce-form-grid">
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">期間起</span>
                    <input type="datetime-local" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} disabled={busy} />
                  </label>
                  <label className="adm-field">
                    <span className="adm-field-label text-secondary">期間迄</span>
                    <input type="datetime-local" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} disabled={busy} />
                  </label>
                </div>
                <div className="section-head">
                  <h3 className="section-title">比對列({importRows.length}/99)</h3>
                  <button className="btn-ghost btn-sm" disabled={busy || importRows.length >= 99} onClick={() => setImportRows((rows) => [...rows, { ...EMPTY_ITEM_ROW }])}>
                    新增列
                  </button>
                </div>
                <div className="commerce-rows-editor">
                  {importRows.map((row, i) => (
                    <div className="commerce-row-line commerce-row-line--recon" key={i}>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">類別</span>
                        <input value={row.kind} onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, kind: e.target.value } : r)))} placeholder="amount_mismatch" disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">實體類型</span>
                        <input value={row.entity_type} onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, entity_type: e.target.value } : r)))} placeholder="invoice / payment" disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">實體 ID(金流方參照)</span>
                        <input value={row.entity_id} onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, entity_id: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">幣別</span>
                        <input value={row.currency} onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, currency: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">平台應為(minor)</span>
                        <input value={row.expected} inputMode="numeric" onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, expected: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label text-secondary">provider 實為(minor)</span>
                        <input value={row.actual} inputMode="numeric" onChange={(e) => setImportRows((rows) => rows.map((r, j) => (j === i ? { ...r, actual: e.target.value } : r)))} disabled={busy} />
                      </label>
                      <button className="btn-ghost btn-sm" disabled={busy || importRows.length <= 1} onClick={() => setImportRows((rows) => rows.filter((_, j) => j !== i))}>
                        移除
                      </button>
                    </div>
                  ))}
                </div>
                <button className="btn-primary" disabled={busy || !importReady} onClick={() => { setErr(''); setConfirmImport(true); }}>
                  匯入 run
                </button>
                <span className="commerce-hint text-tertiary">
                  同 provider + key + 相同輸入重送 = 冪等回放既有 run(可當 retry);同 key 不同輸入會被 409 擋下。
                </span>
              </div>
            ) : (
              <p className="commerce-hint text-tertiary">目前角色僅能檢視對帳差異;匯入與工作流由 admin / platform-owner 執行。</p>
            )}
          </section>
        </aside>
      </div>

      {confirmImport && (
        <ConfirmDialog
          title="匯入對帳 run"
          tone="primary"
          confirmLabel="確認匯入"
          busy={busy}
          error={err || undefined}
          body={
            <>
              <p>
                將匯入 <span className="adm-mono">{provider.trim()}</span> 對帳 run:期間 {periodStart.replace('T', ' ')} → {periodEnd.replace('T', ' ')},
                比對列 {importRows.length} 筆(僅 delta≠0 者入差異清單)。
              </p>
              <p className="text-tertiary">
                冪等鍵:<span className="adm-mono">{idemKey.trim()}</span>(匯入寫入稽核 admin.reconciliation.create)
              </p>
            </>
          }
          onConfirm={() => void submitImport()}
          onCancel={() => setConfirmImport(false)}
        />
      )}

      {confirmResolve && selectedItem && (
        <ConfirmDialog
          title="結案對帳差異"
          tone={resolution === 'adjusted' ? 'danger' : 'primary'}
          confirmLabel="確認結案"
          busy={busy}
          error={err || undefined}
          warning={resolution === 'adjusted' ? `結案將綁定 adjustment 交易 ${adjustmentTxId.trim()};ledger append-only,綁錯需以新分錄更正。${DUAL_CONTROL_NOTE}` : '結案後此差異項不可重開;如需更正,以新 run 匯入。'}
          body={
            <>
              <p>
                將以「{RESOLUTIONS.find((r) => r.value === resolution)?.label}」結案 <span className="adm-mono">{selectedItem.item_id}</span>(
                {selectedItem.entity_type}:{selectedItem.entity_id},差額 {formatSignedMinor(selectedItem.delta_minor, selectedItem.currency)})。
              </p>
              <p className="text-tertiary">處置理由:{resolveReason.trim()}・案件:{resolveCaseId.trim()}(將寫入稽核,含 actor)</p>
            </>
          }
          onConfirm={() => void submitResolve()}
          onCancel={() => setConfirmResolve(false)}
        />
      )}
    </div>
  );
}
